import React, { Fragment, memo, useLayoutEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Chip, Tooltip } from '@mui/material'
import { countFittingChips } from 'src/utils/countFittingChips'

interface Props {
  labels: string[]
}

/**
 * Keeps the counter's focus and key events from reaching the Select that renders this list. MUI's
 * InputBase counts a focused child as a focused field even when disabled, then "blurs" it by
 * calling `onBlur()` with no event, which crashes Formik's `handleBlur`. And SelectInput's
 * `onKeyDown` ignores `disabled`, so Enter or Space here would open a read-only field's menu.
 */
const stopEventPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const stopKeyDownPropagation = (event: React.KeyboardEvent) => {
  // Escape must still reach the document listener that closes the tooltip.
  if (event.key !== 'Escape') {
    event.stopPropagation()
  }
}

/**
 * One row of small chips that never wraps: as many chips as fit the width are shown, the rest
 * collapse into a "+N" chip whose tooltip lists every label. Meant for a multi-select's
 * `renderValue`, where wrapped chips spill out of the fixed-height field.
 */
const OverflowChipListComponent = ({ labels }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(labels.length)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current

    if (!container || !measure) {
      return undefined
    }

    const updateVisibleCount = () => {
      const measuredNodes = Array.from(measure.children) as HTMLElement[]
      // The measure row holds every chip at its natural width, then the "+N" counter last.
      const counterNode = measuredNodes[measuredNodes.length - 1]
      const chipWidths = measuredNodes.slice(0, -1).map((node) => {
        return node.offsetWidth
      })

      setVisibleCount(
        countFittingChips({
          chipWidths,
          counterWidth: counterNode.offsetWidth,
          gap: parseFloat(window.getComputedStyle(measure).columnGap) || 0,
          availableWidth: container.clientWidth,
        }),
      )
    }

    updateVisibleCount()

    const observer = new ResizeObserver(updateVisibleCount)

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [labels])

  const visibleLabels = labels.slice(0, visibleCount)
  const hiddenCount = labels.length - visibleLabels.length

  return (
    <div
      ref={containerRef}
      css={tw`relative flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden`}
    >
      {/* Off-screen copy at natural widths, so the visible row can be sized without a feedback
          loop. The counter uses the full count: the widest label it can ever show. */}
      <div
        ref={measureRef}
        aria-hidden
        css={tw`pointer-events-none invisible absolute left-0 top-0 flex w-max gap-1`}
      >
        {labels.map((label) => {
          return <Chip key={label} label={label} size="small" css={tw`flex-shrink-0`} />
        })}
        <Chip label={`+${labels.length}`} size="small" css={tw`flex-shrink-0`} />
      </div>
      {visibleLabels.map((label) => {
        return <Chip key={label} label={label} size="small" css={tw`min-w-0`} />
      })}
      {hiddenCount > 0 ? (
        <Tooltip
          title={
            <Fragment>
              {labels.map((label) => {
                return <div key={label}>{label}</div>
              })}
            </Fragment>
          }
        >
          {/* Focusable so keyboard users can reach the hidden values: a read-only field disables
              its Select, which takes the whole field out of the tab order. The visually hidden
              text names those values for screen readers, which skip the tooltip. */}
          <Chip
            tabIndex={0}
            onFocus={stopEventPropagation}
            onBlur={stopEventPropagation}
            onKeyDown={stopKeyDownPropagation}
            size="small"
            css={tw`flex-shrink-0`}
            label={
              <Fragment>
                {`+${hiddenCount}`}
                <span css={tw`sr-only`}>{` more: ${labels.slice(visibleCount).join(', ')}`}</span>
              </Fragment>
            }
          />
        </Tooltip>
      ) : null}
    </div>
  )
}

export const OverflowChipList = memo(OverflowChipListComponent)
